import { ApiError } from "./client";

// extractErrorMessage is the single place in the app that turns an
// unknown thrown value into a string safe to show in a toast or inline
// error banner. The resolution order is:
//
//   1. ApiError with a body.message — the server's own human-readable
//      text (backend handlers emit `{error, message, requestId}`).
//   2. ApiError without a useful body — derive from status so the user
//      at least sees the class of failure (e.g. "Conflict (409)").
//   3. A native Error — use .message.
//   4. Anything else — fall back to a caller-supplied generic.
//
// The goal: never show "[object Object]" and never leak a stack trace.
export function extractErrorMessage(
  err: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      message?: string;
      error?: string | { code?: string; message?: string };
    } | undefined;
    if (body?.message && typeof body.message === "string") {
      return body.message;
    }
    if (body?.error && typeof body.error === "object" && typeof body.error.message === "string") {
      return body.error.message;
    }
    if (body?.error && typeof body.error === "string") {
      return `${body.error} (${err.status})`;
    }
    if (body?.error && typeof body.error === "object" && typeof body.error.code === "string") {
      return `${body.error.code} (${err.status})`;
    }
    return `Request failed (${err.status})`;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  if (typeof err === "string" && err.length > 0) {
    return err;
  }
  return fallback;
}
